import { useEffect } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import type { BadgeProps } from '@wartek-id/badge'

import { useAPIGetAnnualEvaluationResult } from 'api/kinerja/useAPIKinerja'
import { Header } from 'app/KinerjaShared/components/Header'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { EvaluationResult } from 'app/KinerjaShared/components/EvaluationResult'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import BannerStatusPengaliranAkhir from 'app/KinerjaPage/components/BannerStatusPengaliranAkhir'
import { NotFoundError } from 'utils/customError'
import {
  PERILAKU_KERJA_RATING_LABEL,
  PRAKTIK_KINERJA_RATING_LABEL,
  PREDIKAT_KINERJA_LABEL,
} from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import { PANDUAN_GENERAL_URL } from 'app/KinerjaShared/utils/panduan'

import type {
  TWorkBehaviorResult,
  TPerformancePracticeResult,
} from 'types/kinerja-pelaksanaan'

export function HasilPenilaianKinerjaV2() {
  const { periodYear: currentYear } = usePeriodContext()

  const { data: evaluationResultData, isLoading } =
    useAPIGetAnnualEvaluationResult({
      params: { year: currentYear },
    })

  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const annualResult = evaluationResultData?.data?.annualResult
  const predicate = annualResult?.predicate?.result
  const workBehavior = annualResult?.predicate?.workBehavior
  const performancePractice = annualResult?.predicate?.performancePractice

  useEffect(() => {
    if (!isLoading && (!annualResult || annualResult.locked)) {
      throw new NotFoundError('', 'Not Found')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualResult, isLoading])

  useEffect(() => {
    if (!annualResult || annualResult.locked) return

    const selectedPeriodRange = 'Tahunan'

    trackEvent({
      activity_type: 'open_hasil_penilaian_kinerja',
      screen_name: 'homepage_pengelolaan',
      attributes: {
        period_selected: selectedPeriodRange,
      },
    })
  }, [trackEvent, annualResult])

  return (
    <main className="bg-gray-10 min-h-full">
      <header className="bg-white pt-11 pb-6 z-[2] relative">
        <div className="max-w-7xl mx-auto px-8">
          <Header
            title="Hasil Penilaian Kinerja"
            rightNode={
              <Button
                className="flex gap-1.5 !py-4 rounded print:hidden"
                variant="ghost"
                as="a"
                type={undefined}
                href={PANDUAN_GENERAL_URL}
                target="blank"
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                Ke Pusat Informasi
              </Button>
            }
          />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-11 w-full flex flex-col space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center px-9 pt-9 gap-5 h-96">
            <Spinner />
            <Text variant="heading-md">Dokumen sedang disiapkan.</Text>
          </div>
        ) : (
          <>
            <BannerStatusPengaliranAkhir year={currentYear} />
            <EvaluationResult
              title="Hasil Penilaian Kinerja Guru"
              dinas={annualResult.dinas.wilayah}
              subordinate={annualResult.teacher}
              supervisor={annualResult.supervisor}
              year={currentYear}
              predicate={PREDIKAT_KINERJA_LABEL[predicate]}
              workBehaviorLabel={<WorkBehaviorBadge value={workBehavior} />}
              performancePracticeLabel={
                <PerformancePracticeBadge value={performancePractice} />
              }
            />
          </>
        )}
      </div>
    </main>
  )
}

const BADGE_VARIANTS: Record<
  TWorkBehaviorResult | TPerformancePracticeResult,
  BadgeProps['variant']
> = {
  MEET_EXPECTATION: 'neutral',
  EXCEED_EXPECTATION: 'success',
  BELOW_EXPECTATION: 'critical',
}

function WorkBehaviorBadge({ value }: { value: TWorkBehaviorResult }) {
  return (
    <Badge variant={BADGE_VARIANTS[value] || 'neutral'}>
      {PERILAKU_KERJA_RATING_LABEL[value] || '-'}
    </Badge>
  )
}

function PerformancePracticeBadge({
  value,
}: {
  value: TPerformancePracticeResult
}) {
  return (
    <Badge variant={BADGE_VARIANTS[value] || 'neutral'}>
      {PRAKTIK_KINERJA_RATING_LABEL[value] || '-'}
    </Badge>
  )
}
